import { ReactNode, createContext, useContext, useState } from 'react'

const VALID_COOKIE_PREFERENCES = ['ALL_COOKIES', 'NECESSARY_COOKIES'] as const

const CookiePreferenceContext = createContext<CookiePreferenceTuple>([
  null,
  () => void 0,
])

const getCookiePreference = () => {
  const cookiePreference =
    typeof window !== 'undefined'
      ? localStorage.getItem('cookiePreference')
      : undefined

  return VALID_COOKIE_PREFERENCES.includes(cookiePreference as CookiePreference)
    ? (cookiePreference as CookiePreference)
    : null
}

export type CookiePreference = (typeof VALID_COOKIE_PREFERENCES)[number]

export type CookiePreferenceTuple = [
  CookiePreference | null,
  (preference: CookiePreference) => void,
]

export const CookiePreferenceContextProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const [cookiePreference, setCookiePreference] = useState(
    getCookiePreference(),
  )

  const storeCookiePreference = (preference: CookiePreference) => {
    if (VALID_COOKIE_PREFERENCES.includes(preference)) {
      localStorage.setItem('cookiePreference', preference)
      setCookiePreference(preference)
    }
  }

  return (
    <CookiePreferenceContext.Provider
      value={[cookiePreference, storeCookiePreference]}
    >
      {children}
    </CookiePreferenceContext.Provider>
  )
}

export const useCookiePreferenceContext = () => {
  const context = useContext(CookiePreferenceContext)
  if (!context) {
    throw new Error(
      'useCookiePreferenceContext must be used within CookiePreferenceContextProvider',
    )
  }
  return context
}
